'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, Smartphone } from 'lucide-react';
import { useUser } from '@/hooks/use-user';
import { useTenant } from '@/providers/tenant-context';
import { useDevices } from '@/hooks/useDeviceRegistration';
import {
  useReactNativeBridge,
  useReactNativeMessageHandler,
  type RNMessage,
} from '@/hooks/useReactNativeBridge';
import { registerDeviceToken } from '@/hooks/useDeviceRegistration';
import { NotificationPermissionContext } from '@/contexts/notification-permission-context';
import {
  clearPendingNotificationPrompt,
  hasPendingNotificationPrompt,
  setNotificationPromptState,
} from '@/lib/notification-prompt-state';
import { shouldAutoShowNotificationPrompt } from '@/lib/notification-prompt-eligibility';
import { logNotificationPromptEvent } from '@/lib/notification-prompt-events';
import type { NativeNotificationPermissionStatus } from '@/lib/native-notification-permission';
import { nativeOsNotificationsEnabled } from '@/lib/native-notification-permission';
import { parseNativeDeepLinkPath } from '@/lib/native-deep-link';
import { detectNativeDeviceType } from '@/lib/native-device-type';
import { toast } from 'sonner';

export function NotificationPermissionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { tenant } = useTenant();
  const appName = tenant?.name ?? 'Milora';
  const {
    devices,
    isLoading: devicesLoading,
    isError: devicesError,
    mutate: mutateDevices,
  } = useDevices();
  const {
    requestNotificationPermission,
    queryNotificationPermissionStatus,
    requestFcmToken,
    openNotificationSettings,
    requestNativeAppInfo,
    isNativeApp,
  } = useReactNativeBridge();
  const [manualPromptOpen, setManualPromptOpen] = useState(false);
  const [promptSuppressedUserId, setPromptSuppressedUserId] = useState<string | null>(null);
  const [promptStateVersion, setPromptStateVersion] = useState(0);
  const [nativePermissionStatus, setNativePermissionStatus] =
    useState<NativeNotificationPermissionStatus>('unknown');
  const manualRequestRef = useRef(false);
  const skipDismissOnCloseRef = useRef(false);
  const promptShownLoggedRef = useRef(false);
  const pendingFcmTokenRef = useRef<string | null>(null);

  const devicesSettled = !devicesLoading || Boolean(devicesError);

  const hasRegisteredDevice = useMemo(
    () => devices.some((device) => device.isActive && device.notificationsEnabled),
    [devices]
  );

  const resolvedNativePermissionStatus: NativeNotificationPermissionStatus =
    isNativeApp && user?.id ? nativePermissionStatus : 'unknown';

  const hasPushNotificationsEnabled = useMemo(() => {
    if (!isNativeApp) {
      return true;
    }
    return nativeOsNotificationsEnabled(resolvedNativePermissionStatus) && hasRegisteredDevice;
  }, [isNativeApp, resolvedNativePermissionStatus, hasRegisteredDevice]);

  const shouldShowAutoPrompt = useMemo(() => {
    void promptStateVersion;

    return shouldAutoShowNotificationPrompt({
      userId: user?.id,
      devicesSettled,
      hasRegisteredDevice,
      isNativeApp,
      nativePermissionStatus: resolvedNativePermissionStatus,
    });
  }, [
    user,
    devicesSettled,
    hasRegisteredDevice,
    isNativeApp,
    resolvedNativePermissionStatus,
    promptStateVersion,
  ]);

  const promptSuppressionKey = user?.id ?? '__anonymous__';
  const isPromptSuppressed =
    promptSuppressedUserId === promptSuppressionKey && !hasPendingNotificationPrompt();

  const isPromptOpen =
    isNativeApp && !isPromptSuppressed && (manualPromptOpen || shouldShowAutoPrompt);

  const nativePermissionDenied = isNativeApp && resolvedNativePermissionStatus === 'denied';

  const bumpPromptState = useCallback(() => {
    setPromptStateVersion((version) => version + 1);
  }, []);

  const requestPushNotifications = useCallback(() => {
    if (!isNativeApp) return;
    manualRequestRef.current = true;
    setPromptSuppressedUserId(null);
    setManualPromptOpen(true);
  }, [isNativeApp]);

  const handleNativePermissionStatus = useCallback((status: NativeNotificationPermissionStatus) => {
    setNativePermissionStatus(status);
  }, []);

  const registerFcmTokenWithBackend = useCallback(
    async (token: string) => {
      if (!user?.id) {
        pendingFcmTokenRef.current = token;
        return;
      }

      try {
        await registerDeviceToken(token, detectNativeDeviceType());
        await mutateDevices();
        pendingFcmTokenRef.current = null;
        setNotificationPromptState(user.id, 'registered');
        clearPendingNotificationPrompt();
        bumpPromptState();
        await logNotificationPromptEvent('token_registered', {
          deviceType: detectNativeDeviceType(),
        });
      } catch (error) {
        pendingFcmTokenRef.current = token;
        await logNotificationPromptEvent('token_registration_failed', {
          message: error instanceof Error ? error.message : 'unknown',
        });
        throw error;
      }
    },
    [user, mutateDevices, bumpPromptState]
  );

  const handleFcmToken = useCallback(
    async (token: string) => {
      try {
        await registerFcmTokenWithBackend(token);
      } catch (error) {
        console.error('[FCM] Failed to register device token:', error);
      }
    },
    [registerFcmTokenWithBackend]
  );

  const handleFcmTokenError = useCallback((message: string) => {
    console.error('[FCM] Native token acquisition failed:', message);
    toast.error('Could not enable push notifications', {
      description: message,
    });
  }, []);

  const navigateToDeepLink = useCallback(
    (data: RNMessage, source: string) => {
      const path = parseNativeDeepLinkPath(data.url, data.path);
      if (path) {
        navigate(path);
        return;
      }
      console.warn(`[${source}] Tap without valid deep-link url; ignoring`, data.url, data.path);
    },
    [navigate]
  );

  const bridgeMessageHandlers = useMemo(
    () => ({
      NOTIFICATION_TAPPED: (data: RNMessage) => {
        navigateToDeepLink(data, 'Notifications');
      },
      DEEPLINK: (data: RNMessage) => {
        navigateToDeepLink(data, 'DeepLink');
      },
    }),
    [navigateToDeepLink]
  );

  useReactNativeMessageHandler({
    onFcmToken: handleFcmToken,
    onFcmTokenError: handleFcmTokenError,
    onNotificationPermissionStatus: handleNativePermissionStatus,
    handlers: bridgeMessageHandlers,
  });

  useEffect(() => {
    if (!isNativeApp) return;
    requestNativeAppInfo();
  }, [isNativeApp, requestNativeAppInfo]);

  useEffect(() => {
    if (!isNativeApp || !user?.id) return;
    queryNotificationPermissionStatus();
  }, [isNativeApp, user?.id, queryNotificationPermissionStatus]);

  useEffect(() => {
    if (!isNativeApp || !user?.id) return;
    if (!pendingFcmTokenRef.current) return;
    void registerFcmTokenWithBackend(pendingFcmTokenRef.current);
  }, [isNativeApp, user?.id, registerFcmTokenWithBackend]);

  useEffect(() => {
    if (!isNativeApp || !user?.id || !devicesSettled) return;
    if (hasRegisteredDevice) return;
    if (!nativeOsNotificationsEnabled(resolvedNativePermissionStatus)) return;
    requestFcmToken();
  }, [
    isNativeApp,
    user?.id,
    devicesSettled,
    hasRegisteredDevice,
    resolvedNativePermissionStatus,
    requestFcmToken,
  ]);

  useEffect(() => {
    if (!user?.id || !devicesSettled) return;

    if (
      hasRegisteredDevice &&
      (!isNativeApp || nativeOsNotificationsEnabled(resolvedNativePermissionStatus))
    ) {
      setNotificationPromptState(user.id, 'registered');
      clearPendingNotificationPrompt();
      return;
    }

    if (hasPendingNotificationPrompt()) {
      clearPendingNotificationPrompt();
      setNotificationPromptState(user.id, 'pending');
    }
  }, [user, devicesSettled, hasRegisteredDevice, isNativeApp, resolvedNativePermissionStatus]);

  useEffect(() => {
    if (!isPromptOpen) {
      promptShownLoggedRef.current = false;
      return;
    }

    if (promptShownLoggedRef.current) return;
    promptShownLoggedRef.current = true;
    void logNotificationPromptEvent('prompt_shown', { native: isNativeApp });
  }, [isPromptOpen, isNativeApp]);

  const handleAccept = useCallback(() => {
    if (!user?.id || !isNativeApp) return;

    skipDismissOnCloseRef.current = true;
    setPromptSuppressedUserId(promptSuppressionKey);
    setNotificationPromptState(user.id, 'prompted');
    bumpPromptState();
    void logNotificationPromptEvent('prompt_accepted', { native: true });
    setManualPromptOpen(false);
    manualRequestRef.current = false;

    if (nativePermissionDenied) {
      openNotificationSettings();
    } else {
      requestNotificationPermission();
    }
  }, [
    user,
    isNativeApp,
    nativePermissionDenied,
    requestNotificationPermission,
    openNotificationSettings,
    bumpPromptState,
    promptSuppressionKey,
  ]);

  const handleDismiss = useCallback(() => {
    const wasManual = manualRequestRef.current;

    setPromptSuppressedUserId(promptSuppressionKey);
    setManualPromptOpen(false);
    manualRequestRef.current = false;

    if (user?.id) {
      if (!wasManual) {
        setNotificationPromptState(user.id, 'dismissed');
        clearPendingNotificationPrompt();
        bumpPromptState();
      }
      void logNotificationPromptEvent('prompt_dismissed', {
        manual: wasManual,
        native: isNativeApp,
      });
    }
  }, [user, isNativeApp, bumpPromptState, promptSuppressionKey]);

  const value = useMemo(
    () => ({
      isNativeApp,
      hasPushNotificationsEnabled,
      devicesLoading,
      requestPushNotifications,
    }),
    [isNativeApp, hasPushNotificationsEnabled, devicesLoading, requestPushNotifications]
  );

  return (
    <NotificationPermissionContext.Provider value={value}>
      {children}
      {isNativeApp && (
        <Dialog
          open={isPromptOpen}
          onOpenChange={(open) => {
            if (open) {
              setManualPromptOpen(true);
              return;
            }

            if (skipDismissOnCloseRef.current) {
              skipDismissOnCloseRef.current = false;
              setManualPromptOpen(false);
              return;
            }

            handleDismiss();
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Enable notifications
              </DialogTitle>
              <DialogDescription>
                {nativePermissionDenied
                  ? `Notifications are turned off for ${appName} on this device. Allow notifications so you do not miss events and community updates.`
                  : `${appName} will ask you to allow notifications on this device for events, community activity, and important announcements.`}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>
                Tap Allow below, then confirm when your phone shows the system notification prompt.
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleDismiss}>
                Not now
              </Button>
              <Button type="button" onClick={handleAccept}>
                {nativePermissionDenied ? 'Open settings' : 'Allow notifications'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </NotificationPermissionContext.Provider>
  );
}
